import { isEqual, get } from 'lodash';
import { default as _Vue } from 'vue';
import { GetterTree, Module, MutationTree, Store } from 'vuex';
import { Consola } from 'consola';
import { Severity } from '@sentry/types';

export type TEntity<E> = E & {
  _id: string;
  createdAt: string;
  updatedAt: string;
};

export type TFetch<E> = {
  data: TEntity<E>[];
  limit: number;
  skip: number;
  total: number;
};

export type TQuery = {
  $limit: number;
  $skip: number;
  $sort: any;
  $select: string[];
  $or?: any;
};

export type TSyncOptions = {
  mergeWithLastUsedQuery?: boolean;
  skipIfTheQueryIsTheSameAsThePreviousOne?: boolean;
};

export type TSyncPayload = TSyncOptions & {
  query?: Partial<TQuery>;
};

export type TState<TEntity> = {
  list: TEntity[];
  total: number;
  loading: boolean;
};

export interface IEntity<E> {
  readonly defaultState: TState<TEntity<E>>;
  readonly mutations: MutationTree<TState<TEntity<E>>>;
  readonly getters: GetterTree<TState<TEntity<E>>, any>;
}

/**
 * @author Damien CORLER <d.corler@zhortech.com>
 * @since 1.0.0
 * @version 1.0.0
 */
export default abstract class Entity<E> implements IEntity<E> {
  // Private variables
  protected readonly Vue: typeof _Vue;
  protected readonly store: Store<any>;
  protected readonly logger: Consola;

  // Entity name (better if pluralized)
  protected name: string = 'N/A';

  // Default query params
  defaultQuery: Partial<TQuery>;

  // History of the query
  private _lastUsedQuery: Partial<TQuery> = {};

  /**
   * @param Vue
   * @param store
   * @param logger
   * @param options
   */
  constructor(
    Vue: typeof _Vue,
    store: Store<any>,
    logger: Consola,
    options: {
      queryParams: Partial<TQuery>;
    },
  ) {
    this.Vue = Vue;
    this.store = store;
    this.logger = logger;
    this.defaultQuery = options.queryParams;
  }

  /**
   * Get default state of this store module
   */
  public get defaultState(): TState<TEntity<E>> {
    return {
      list: [],
      total: 0,
      loading: false,
    };
  }

  public get mutations(): MutationTree<TState<TEntity<E>>> {
    return {
      SET(state, value: any) {
        state.list = value;
      },
      CREATE_ONE(state, ressource: TEntity<E>) {
        if (!state.list.find((x) => x._id === x._id)) {
          state.list.push(ressource);

          // TODO : find better solution
          state.total++;
        }
      },
      PATCH_ONE(state, ressource: TEntity<E>) {
        const currentRessource = state.list.find((x) => x._id === ressource._id);
        if (!currentRessource) {
          state.list.push(ressource);

          // TODO : find better solution
          state.total++;
        } else {
          const t = state.list.indexOf(currentRessource);
          _Vue.set(state.list, t, { ...currentRessource, ...ressource });
        }
      },
      REMOVE(state, id: string) {
        state.list = state.list.filter((entity) => entity._id !== id);

        // TODO : find better solution
        state.total--;
      },
      SET_TOTAL(state, total: number) {
        state.total = total;
      },
      SET_LOADING(state, loading: boolean) {
        state.loading = loading;
      },
    };
  }

  public get getters(): GetterTree<TState<TEntity<E>>, any> {
    return {
      getWithId: (state) => (id: string) => {
        return state.list.find((x) => x._id === id);
      },
    };
  }

  /**
   * Get store module
   */
  public get storeModule(): Module<TState<TEntity<E>>, any> {
    return {
      namespaced: true,
      state: this.defaultState,
      mutations: this.mutations,
      getters: this.getters,
    };
  }

  public startListener() {
    this.Vue.prototype.$pdc.repository.socket.session.on(`${this.name} created`, (ressource: TEntity<E>) => {
      // Commit
      this.store.commit(`pdc/entities/${this.name}/CREATE_ONE`, ressource);
      // Send global event
      this.Vue.prototype.$pdc.bus.emitDebug(`entities:${this.name}:created`, ressource);
      // Log
      this.logger.log(`Entity "${this.name}" created`);
      // Return resource
      return ressource;
    });

    this.Vue.prototype.$pdc.repository.socket.session.on(`${this.name} patched`, (ressource: TEntity<E>) => {
      // TODO : remove this quickly
      if (this.name === 'stations' && !this.Vue.prototype.$pdc.entities!.stations.storedEntity.list.length) {
        window.location.reload();
      }

      // Commit
      this.store.commit(`pdc/entities/${this.name}/PATCH_ONE`, ressource);
      // Send global event
      this.Vue.prototype.$pdc.bus.emitDebug(`entities:${this.name}:patched`, ressource);
      // Log
      this.logger.log(`Entity "${this.name}" patched`);
      // Return resource
      return ressource;
    });

    this.Vue.prototype.$pdc.repository.socket.session.on(`${this.name} removed`, (ressource: TEntity<E>) => {
      // Commit
      this.store.commit(`pdc/entities/${this.name}/REMOVE`, ressource);
      // Send global event
      this.Vue.prototype.$pdc.bus.emitDebug(`entities:${this.name}:removed`, ressource);
      // Log
      this.logger.log(`Entity "${this.name}" removed`);
      // Return resource
      return ressource;
    });

    this.extendListeners();

    this.logger.withTag(this.name).info('Listen the cloud');
  }

  /**
   * Complete this function to extend listeners
   */
  public extendListeners() {}

  /**
   * Sync entity with the cloud
   */
  public async sync({
    query = this.defaultQuery || {},
    mergeWithLastUsedQuery,
    skipIfTheQueryIsTheSameAsThePreviousOne,
  }: TSyncPayload): Promise<void> {
    try {
      // Bus
      this.Vue.prototype.$pdc.bus.emitDebug(`entities:${this.name}-sync-pending`);
      // Set loading flag
      this.store.commit(`pdc/entities/${this.name}/SET_LOADING`, true);
      // Start timer
      const t0 = performance.now();
      // Log
      this.logger.withTag(this.name).log(`Syncing ...`);
      // Check if we can skip ...
      if (skipIfTheQueryIsTheSameAsThePreviousOne && isEqual(query, this.lastUsedQuery)) {
        this.logger.withTag(this.name).info(`Sync skiped`);
        this.store.commit(`pdc/entities/${this.name}/SET_LOADING`, false);
        return;
      }
      // Parameters
      if (mergeWithLastUsedQuery) {
        query = {
          ...this.lastUsedQuery,
          ...query,
        };
      }
      // Fetch ressource
      const result = await this.Vue.prototype.$pdc.repository.fetchRessource({
        ressource: this.name,
        params: query,
      });
      // Save result into the store
      this.store.commit(`pdc/entities/${this.name}/SET`, result.data);
      // Save total into the store
      this.store.commit(`pdc/entities/${this.name}/SET_TOTAL`, result.total);
      // Save query
      this._lastUsedQuery = query;
      // Stop timer
      const t1 = performance.now();
      // Log
      this.logger.withTag(this.name).info(`Synced in ${Math.round(t1 - t0)}ms`);
      // Bus
      this.Vue.prototype.$pdc.bus.emitDebug(`entities:${this.name}-sync-completed`);
      // Return result
      return result;
    } catch (e) {
      // Bus
      this.Vue.prototype.$pdc.bus.emitDebug(`entities:${this.name}-sync-failed`, e);
      // Log
      this.logger.withTag(this.name).fatal('Failed to sync');
      // Throw
      throw e;
    } finally {
      this.store.commit(`pdc/entities/${this.name}/SET_LOADING`, false);
    }
  }

  get storedEntity(): TState<TEntity<E>> {
    return this.store.state.pdc.entities[this.name];
  }

  public async remove(id: string) {
    const result = await this.Vue.prototype.$pdc.repository!.deleteRessource({
      ressource: this.name,
      identifier: id,
    });
    this.store.commit(`pdc/entities/${this.name}/REMOVE`, id);
    return result;
  }

  public getStoredEntityById(id: string): TEntity<E> | null {
    return this.store.getters[`pdc/entities/${this.name}/getWithId`](id);
  }

  public static convertObjectToQuery(params: Partial<TQuery>): object {
    return Object.entries(params).reduce((acc: any, [key, val]) => {
      if (typeof val !== 'undefined') {
        acc[`${key}`] = val;
      }
      return acc;
    }, {});
  }

  get lastUsedQuery(): Partial<TQuery> {
    return this._lastUsedQuery;
  }

  public $clean() {
    const _state = this.store.state.pdc.entities[this.name];

    this.logger.withTag(this.name).info('CLEANUP');

    Object.keys(this.defaultState).forEach((key) => {
      _state[key] = (this.defaultState as any)[key];
      // this.logger.log('RESET', this.name, key, (this.defaultState as any)[key]);
    });
  }
}
